import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { useI18n } from "../context/I18nContext";

const ROLES = [
  { value: "cliente", label: "Cliente" },
  { value: "arquitecto", label: "Arquitecto" },
  { value: "disenador", label: "Diseñador" },
  { value: "interior_designer", label: "Interior Designer" },
  { value: "ventas", label: "Ventas" },
  { value: "cuentas", label: "Cuentas" },
  { value: "contador", label: "Contador" },
  { value: "produccion", label: "Producción" },
  { value: "fabricante", label: "Fabricante" },
  { value: "retailer", label: "Retailer" },
  { value: "proveedor", label: "Proveedor" },
];

export default function Register() {
  const { register } = useAuth();
  const { t } = useI18n();
  const [form, setForm] = useState({ first_name: "", last_name: "", username: "", personal_email: "", password: "", role: "cliente" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register(form);
      setDone(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-cream">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-md"
      >
        <Link to="/" className="font-sans text-xl tracking-[0.25em] block mb-10">OMNIA</Link>

        {done ? (
          <div data-testid="register-pending" className="bg-white border border-navy/10 rounded-xl p-8 text-center">
            <div className="w-14 h-14 rounded-full bg-gradient-violet mx-auto flex items-center justify-center text-white text-2xl">✓</div>
            <h2 className="text-2xl font-medium tracking-tight mt-5">{t("auth.requestSentTitle")}</h2>
            <p className="text-navy/60 mt-3 leading-relaxed">
              {t("auth.requestSentBody")}
            </p>
            <Link to="/login" className="inline-flex items-center gap-2 mt-6 text-violet hover:underline">{t("auth.backToLogin")} <ArrowRight className="w-4 h-4" /></Link>
          </div>
        ) : (
          <>
            <div className="overline text-navy/40 mb-3">{t("auth.byInvitation")}</div>
            <h2 className="text-3xl font-medium tracking-tight mb-2">{t("auth.requestAccess")}</h2>
            <p className="text-sm text-navy/50 mb-8">{t("auth.requestAccessSub")}</p>

            <form onSubmit={submit} className="space-y-5" data-testid="register-form">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="overline text-navy/50">{t("auth.firstName")}</label>
                  <input data-testid="reg-first-name" value={form.first_name} onChange={set("first_name")} required
                    className="mt-2 w-full bg-white border border-navy/10 rounded-lg px-4 py-3 outline-none focus:border-violet transition-colors" />
                </div>
                <div>
                  <label className="overline text-navy/50">{t("auth.lastName")}</label>
                  <input data-testid="reg-last-name" value={form.last_name} onChange={set("last_name")} required
                    className="mt-2 w-full bg-white border border-navy/10 rounded-lg px-4 py-3 outline-none focus:border-violet transition-colors" />
                </div>
              </div>
              <div>
                <label className="overline text-navy/50">{t("auth.user")}</label>
                <div className="mt-2 flex items-center bg-white border border-navy/10 rounded-lg px-4 focus-within:border-violet transition-colors">
                  <input data-testid="reg-email" type="text" value={form.username} onChange={set("username")} required
                    autoCapitalize="none" className="flex-1 bg-transparent py-3 outline-none" placeholder="oscar" />
                  <span className="text-navy/40 select-none">@omnia.os</span>
                </div>
                <p className="mt-1.5 text-xs text-navy/40">{t("auth.yourAccessWillBe")} <b>{(form.username.trim().toLowerCase().split("@")[0] || "usuario")}@omnia.os</b></p>
              </div>
              <div>
                <label className="overline text-navy/50">{t("auth.personalEmail")}</label>
                <input data-testid="reg-personal-email" type="email" value={form.personal_email} onChange={set("personal_email")} required
                  autoCapitalize="none" placeholder="tucorreo@gmail.com"
                  className="mt-2 w-full bg-white border border-navy/10 rounded-lg px-4 py-3 outline-none focus:border-violet transition-colors" />
                <p className="mt-1.5 text-xs text-navy/40">{t("auth.personalEmailHint")}</p>
              </div>
              <div>
                <label className="overline text-navy/50">{t("auth.password")}</label>
                <input data-testid="reg-password" type="password" value={form.password} onChange={set("password")} required minLength={6}
                  className="mt-2 w-full bg-white border border-navy/10 rounded-lg px-4 py-3 outline-none focus:border-violet transition-colors" />
              </div>
              <div>
                <label className="overline text-navy/50">{t("auth.profile")}</label>
                <select data-testid="reg-role" value={form.role} onChange={set("role")}
                  className="mt-2 w-full bg-white border border-navy/10 rounded-lg px-4 py-3 outline-none focus:border-violet transition-colors">
                  {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>

              {error && <p data-testid="register-error" className="text-sm text-destructive">{error}</p>}

              <button data-testid="register-submit" disabled={loading}
                className="group w-full inline-flex items-center justify-center gap-2 rounded-full bg-gradient-violet text-white py-3.5 hover:opacity-90 transition-opacity disabled:opacity-60">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t("auth.sendRequest")}
                {!loading && <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />}
              </button>
            </form>

            <p className="mt-6 text-sm text-navy/50">
              {t("auth.alreadyAccount")}{" "}
              <Link to="/login" data-testid="link-login" className="text-violet hover:underline">{t("auth.signIn")}</Link>
            </p>
            <p className="mt-2 text-sm text-navy/50">
              {t("auth.createOwnCompanyQ")}{" "}
              <Link to="/register-company" data-testid="link-register-company" className="text-violet hover:underline">{t("auth.registerCompanyLink")}</Link>
            </p>
          </>
        )}
      </motion.div>
    </div>
  );
}
